<script setup>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import {onMounted, ref, computed, watch, nextTick, onBeforeUnmount} from "vue";
import {useRoute, useRouter} from "vue-router";
import axios from "axios";
import {useHead} from "@vueuse/head";

const route = useRoute()
const router = useRouter()
const projectList = ref(null)
const current = ref(1)
const isLoading = ref(false)
const loadedScripts = ref(new Set())

const lastPage = computed(() => {
  return Math.ceil(projectList.value?.count / 12) || 1
})

const prevPage = computed(() => {
  if (current.value > 1) {
    return current.value - 1;
  } else {
    return 1
  }
})

const nextPage = computed(() => {
  if (current.value < lastPage.value) {
    return current.value + 1
  } else {
    return current.value
  }
})

const pages = computed(() => {
  const pages = []
  for (let i = 1; i <= lastPage.value; i++) {
    if (i === current.value || i === lastPage.value || (i >= current.value - 1 && i <= current.value + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...')
    }
  }
  return pages
})

function getPageFromUrl() {
  const page = Number(route.query.page);
  return page && page > 0 ? page : 1
}

function getToPage(page) {
  if (page === '...' || page === current.value || isLoading.value) return;

  current.value = page;
  const query = {...route.query};
  query.page = page
  router.push({
    query
  })
}

// 改进的脚本加载函数
function loadScriptsSequentially(scripts, index = 0) {
  return new Promise((resolve) => {
    if (index >= scripts.length) {
      resolve();
      return;
    }

    const scriptSrc = scripts[index];

    // 检查是否已经加载过
    if (loadedScripts.value.has(scriptSrc) || document.querySelector(`script[src="${scriptSrc}"]`)) {
      loadScriptsSequentially(scripts, index + 1).then(resolve);
      return;
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = false; // 确保按顺序执行

    script.onload = () => {
      loadedScripts.value.add(scriptSrc);
      loadScriptsSequentially(scripts, index + 1).then(resolve);
    };

    script.onerror = () => {
      console.warn(`Failed to load script: ${scriptSrc}`);
      loadScriptsSequentially(scripts, index + 1).then(resolve);
    };

    document.head.appendChild(script);
  });
}

async function get_project_list(retries = 3) {
  if (isLoading.value) return;

  isLoading.value = true;
  let url = '/api/projects/'
  const page = Number(route.query.page)
  const title = route.query.project_title;
  const params = new URLSearchParams()

  if (page && page > 0) {
    params.append('page', page)
  }
  if (title) {
    params.append('project_title', title)
  }

  // 添加时间戳防止缓存（可选）
  // params.append('_t', Date.now().toString())

  url = url + '?' + params.toString()

  try {
    const response = await axios.get(url, {
      withCredentials: true,
      // 添加缓存控制头
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    projectList.value = response.data;
  } catch (error) {
    console.error('Failed to fetch project list:', error);
    if (retries > 0) {
      console.warn('Request failed, retrying...', retries);
      await new Promise(resolve => setTimeout(resolve, 1000)); // 等待1秒后重试
      return get_project_list(retries - 1);
    } else {
      throw error;
    }
  } finally {
    isLoading.value = false;
  }
}

onMounted(async () => {
  const scripts = [
      '/assets/js/vendor/modernizr-2.8.3.min.js',
    '/assets/js/vendor/jquery-3.5.1.min.js',
    '/assets/js/bootstrap.min.js',
    '/assets/js/isotope.pkgd.min.js',
    '/assets/js/owl.carousel.min.js',
    '/assets/js/jquery.nivo.slider.pack.js',
    '/assets/js/slick.min.js',
    '/assets/js/imagesloaded.pkgd.min.js',
    '/venobox/venobox.min.js',
    '/assets/js/jquery.appear.js',
    '/assets/js/jquery.knob.js',
    '/assets/js/BeerSlider.js',
    '/assets/js/theme-pluginjs.js',
    '/assets/js/jquery.meanmenu.js',
    '/assets/js/ajax-mail.js',
    '/assets/js/theme.js',
  ];

  try {
    await loadScriptsSequentially(scripts);
    current.value = getPageFromUrl();
    await get_project_list();

    // 确保DOM更新后再初始化插件
    await nextTick();

    // 初始化Isotope和过滤器
    initializeIsotope();

  } catch (error) {
    console.error('Error during component mounting:', error);
  }
})

// 监听路由变化
watch(() => route.query, async (newQuery, oldQuery) => {
  // 避免无意义的重复请求
  if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
    current.value = getPageFromUrl();
    await get_project_list();
  }
}, { immediate: false })

// 初始化Isotope过滤器
function initializeIsotope() {
  if (!window.jQuery || !window.jQuery.fn.isotope) {
    setTimeout(() => initializeIsotope(), 100);
    return;
  }

  const $ = window.jQuery;

  // 等待图片加载完成
  $('.prot_wrap').imagesLoaded(function() {
    // 初始化Isotope
    const $grid = $('.prot_wrap').isotope({
      itemSelector: '.grid-item',
      layoutMode: 'fitRows'
    });

    // 绑定过滤器点击事件
    $('.filter_menu li').off('click').on('click', function() {
      $('.filter_menu li').removeClass('current_menu_item');
      $(this).addClass('current_menu_item');

      const filterValue = $(this).attr('data-filter');
      $grid.isotope({ filter: filterValue });
    });

    // 初始化VenoBox（如果存在）
    if ($.fn.venobox) {
      $('.venobox').venobox();
    }
  });
}

// 重新初始化插件的函数
function reinitializePlugins() {
  setTimeout(() => {
    initializeIsotope();
  }, 300);
}

useHead({
  title: 'TPA Housing - 100000m2 Production Plant, 20 Years Experienced Engineer, 30 Countries House Projects',
  meta: [
    {
      name: 'description',
      content: 'We can provide prefabricated design expertise, construction knowledge you need to build the house you want in short time and within your budget,Steel Structure House Building,Warehouse Steel Structure,Lightweight Steel Structure and other products and services'
    }
  ]
})
</script>

<template>
  <Header/>

  <!-- breadcumb-area -->
  <div class="breadcumb-area">
    <div class="container">
      <div class="row">
        <div class="col-md-12 txtc text-center ccase">
          <div class="brpt">
            <h2>Projects</h2>
          </div>
          <div class="breadcumb-inner">
            <ul>
              <li><a href="/">Home</a></li>
              <li><i class="fas fa-angle-right"></i></li>
              <li>Projects</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- wirr_project_area -->
  <div class="wirr_project_area project4">
    <div class="container">
      <div class="row">
        <div class="col-md-12">
          <div class="witr_section_title">
            <div class="witr_section_title_inner text-center">
              <h3>Our Best Project</h3>
            </div>
          </div>
        </div>
      </div>
      <div class="row protfolio_top">
        <div class="col-md-12">
          <div class="clearfix kicuakta">
            <div class="portfolio_nav wittr_pfilter_menu">
              <ul id="filter" class="filter_menu">
                <li class="current_menu_item" data-filter="*">All Project</li>
                <li data-filter=".steel-structure">Steel Structure</li>
                <li data-filter=".steel-house">Steel House</li>
              </ul>
            </div>
          </div>

          <!-- 添加加载状态 -->
          <div v-if="isLoading" class="loading-container text-center">
            <p>Loading projects...</p>
          </div>

          <div v-else class="em_load pstyle2">
            <div class="prot_wrap">
              <div
                v-if="projectList && projectList.results && projectList.results.length > 0"
                v-for="list in projectList.results"
                :key="list.id || list.site"
                :class="'col-lg-4 grid-item col-md-6 col-sm-12 ' + (list.tag || '') + ' witr_all_mb_30'"
              >
                <div class="single_protfolio">
                  <div class="prot_thumb">
                    <img :src="list.image_1" :alt="list.project_title || 'Project Image'" loading="lazy">
                    <div class="prot_content">
                      <div class="prot_content_inner">
                        <div class="picon">
                          <a class="portfolio-icon venobox vbox-item"
                             data-gall="myportfolio"
                             :href="list.image_1">
                            <i class="fas fa-arrows-alt"></i>
                          </a>
                        </div>
                        <h3>
                          <a :href="'/projects/' + list.site">{{ list.project_title }}</a>
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 无数据状态 -->
              <div v-else class="col-12 text-center">
                <p>No projects found.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- START PAGINATION -->
      <div class="row" v-if="projectList && projectList.results && projectList.results.length > 0">
        <div class="col-md-12">
          <div class="paginations">
            <ul class='page-numbers'>
              <li @click.prevent="getToPage(prevPage)">
                <a class="next page-numbers" href="javascript:void(0)" onclick="location.reload()" :class="{ disabled: current <= 1 }">
                  <i class="fa fa-long-arrow-left"></i>
                </a>
              </li>
              <li v-for="page in pages" :key="page">
                <a
                  @click.prevent="getToPage(page)"
                  class='page-numbers'
                  href="javascript:void(0)" onclick="location.reload()"
                  :class="{
                    current: page === current,
                    disabled: page === '...',
                    active: page === current
                  }"
                >
                  {{ page }}
                </a>
              </li>
              <li @click.prevent="getToPage(nextPage)">
                <a class="next page-numbers" href="javascript:void(0)" onclick="location.reload()" :class="{ disabled: current >= lastPage }">
                  <i class="fa fa-long-arrow-right"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <!-- END START PAGINATION -->
    </div>
  </div>

  <Footer/>
</template>

<style scoped lang="scss">
.loading-container {
  padding: 50px 0;
}

.page-numbers {
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.current,
  &.active {
    background-color: #007bff;
    color: white;
  }
}
</style>